import { useEffect, useRef, useCallback, useState } from "react";
import { useHistory } from "react-router-dom";
import shortcuts from "../data/shortcuts.json";

const SEQUENCE_TIMEOUT = 800; // ms to complete a sequence after first key

/**
 * Gmail-style keyboard shortcuts hook.
 *
 * Sequences work in both EN and ES simultaneously:
 *   "g o" → Go to Orders   |   "i o" → Ir a Órdenes
 *
 * Singles fire on a single keypress: "n", "/", "?", "Escape"
 *
 * All shortcuts are ignored when focus is on an input/textarea/select
 * or inside a contentEditable element.
 *
 * @param {Object} handlers - Optional map of action names to callbacks
 *   e.g. { new_order: () => ..., focus_search: () => ..., show_shortcuts: () => ... }
 */
export const useShortcuts = (handlers = {}, { disabled = false } = {}) => {
  const history = useHistory();
  const bufferRef = useRef([]);
  const timerRef = useRef(null);
  const [showHelp, setShowHelp] = useState(false);

  // Build a lookup of all sequence triggers: { "g": true, "i": true }
  const prefixKeys = useRef(new Set());
  // Build sequence map: "g+o" → shortcut, "i+o" → shortcut
  const sequenceMap = useRef(new Map());

  // Initialize maps once
  useEffect(() => {
    shortcuts.sequences.forEach((seq) => {
      // English keys
      const enKey = seq.keys.join("+");
      sequenceMap.current.set(enKey, seq);
      prefixKeys.current.add(seq.keys[0]);

      // Spanish keys
      if (seq.keys_es) {
        const esKey = seq.keys_es.join("+");
        sequenceMap.current.set(esKey, seq);
        prefixKeys.current.add(seq.keys_es[0]);
      }
    });
  }, []);

  const resetBuffer = useCallback(() => {
    bufferRef.current = [];
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const executeAction = useCallback(
    (shortcut) => {
      if (shortcut.action === "navigate" && shortcut.target) {
        history.push(shortcut.target);
      } else if (shortcut.action === "show_shortcuts") {
        setShowHelp((prev) => !prev);
      } else if (handlers[shortcut.action]) {
        handlers[shortcut.action]();
      }
    },
    [history, handlers],
  );

  useEffect(() => {
    if (disabled) return;

    function onKeyDown(e) {
      const tag = e.target.tagName;
      if (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        e.target.isContentEditable
      ) {
        return;
      }

      // Ignore when modifier keys are held (allow Shift for "?")
      if (e.ctrlKey || e.metaKey || e.altKey) return;

      const key = e.key;

      // --- Sequence handling ---
      if (bufferRef.current.length > 0) {
        // We're mid-sequence — try to complete it
        e.preventDefault();
        bufferRef.current.push(key);
        const combo = bufferRef.current.join("+");
        const matched = sequenceMap.current.get(combo);

        if (matched) {
          resetBuffer();
          executeAction(matched);
        } else {
          // No match — reset
          resetBuffer();
        }
        return;
      }

      // --- Start of a new sequence? ---
      if (prefixKeys.current.has(key)) {
        bufferRef.current = [key];
        timerRef.current = setTimeout(() => {
          resetBuffer();
        }, SEQUENCE_TIMEOUT);
        return;
      }

      // --- Single-key shortcuts ---
      const single = shortcuts.singles.find((s) => s.key === key);
      if (single) {
        executeAction(single);
      }
    }

    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [executeAction, resetBuffer, disabled]);

  return { showHelp, setShowHelp };
};
